import React, { useEffect, useState } from 'react';
import './Header.css';
import useAutoTranslate from '../../hooks/useAutoTranslate';
import fr from './langue/fr.json';
import en from './langue/en.json';

// ── Certifications du carrousel ───────────────────────────────────
// Ajoute / retire une entrée ici, le carrousel s'adapte tout seul.
const certifications = [
  {
    href: `${process.env.PUBLIC_URL}/assets/csna.pdf`,
    img: `${process.env.PUBLIC_URL}/assets/img/techno/csna.png`,
    alt: 'CSNA',
  },
  {
    href: `${process.env.PUBLIC_URL}/assets/certificat_toeic.pdf`,
    img: `${process.env.PUBLIC_URL}/assets/img/techno/toeic.png`,
    alt: 'TOEIC',
  },
  {
    href: `${process.env.PUBLIC_URL}/assets/Label%20Handimanagement.pdf`,
    img: `${process.env.PUBLIC_URL}/assets/img/techno/lh.png`,
    alt: 'Label Handimanagement',
  },
];

const DUREE = 3000; // temps d'affichage d'un logo (ms)

// ── Effet "machine à écrire" ──────────────────────────────────────
const VITESSE_FRAPPE = 65;  // ms entre chaque lettre
const DELAI_DEPART  = 700;  // ms avant de commencer à taper

function Typewriter({ text }) {
  const [count, setCount] = useState(0);

  // Recommence quand le texte change (ex. changement de langue)
  useEffect(() => {
    const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduit) {
      setCount(text.length);
      return undefined;
    }
    setCount(0);
    let timer;
    const start = setTimeout(function taper() {
      setCount((c) => {
        if (c < text.length) {
          timer = setTimeout(taper, VITESSE_FRAPPE);
          return c + 1;
        }
        return c;
      });
    }, DELAI_DEPART);
    return () => {
      clearTimeout(start);
      clearTimeout(timer);
    };
  }, [text]);

  return (
    <span className="typewriter" aria-label={text}>
      <span aria-hidden="true">{text.slice(0, count)}</span>
      <span className="typewriter__cursor" aria-hidden="true"></span>
      {/* Le reste du texte est invisible : il réserve la place pour éviter que la page "saute" */}
      <span className="typewriter__ghost" aria-hidden="true">{text.slice(count)}</span>
    </span>
  );
}

// ── Nom / prénom : décodage + vague lumineuse ─────────────────────
const DEPART_PRENOM = 400; // ms avant le décodage du prénom
const DEPART_NOM    = 800; // ms avant le décodage du nom

function AnimatedName({ prenom, nom }) {
  return (
    <h1 className="header__title" aria-label={`${prenom} ${nom}`}>
      <ScrambleText text={prenom} delay={DEPART_PRENOM} letterClass="name-letter" />
      <br />
      <ScrambleText
        text={nom}
        delay={DEPART_NOM}
        letterClass="name-letter"
        offset={prenom.length}
      />
    </h1>
  );
}

// ── Fonction : effet "décodage" ───────────────────────────────────
const CARACTERES = '!<>-_\\/[]{}=+*^?#01';
const DEPART_DECODAGE = 1600; // ms avant le début du décodage

function ScrambleText({ text, delay = DEPART_DECODAGE, letterClass, offset = 0 }) {
  const [revealed, setRevealed] = useState(-1); // -1 = pas encore commencé
  const [, forceRender] = useState(0);

  useEffect(() => {
    const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduit) {
      setRevealed(text.length);
      return undefined;
    }
    setRevealed(-1);
    let frame = 0;
    let interval;
    const start = setTimeout(() => {
      interval = setInterval(() => {
        frame += 1;
        const r = Math.floor(frame / 2); // 1 lettre révélée toutes les 2 images
        setRevealed(r);
        forceRender((x) => x + 1);       // pour renouveler les caractères aléatoires
        if (r >= text.length) clearInterval(interval);
      }, 35);
    }, delay);
    return () => {
      clearTimeout(start);
      clearInterval(interval);
    };
  }, [text, delay]);

  if (revealed < 0) {
    return <span className="scramble scramble--hidden">{text}</span>;
  }

  return (
    <span className="scramble" aria-label={text}>
      {Array.from(text).map((c, i) => {
        if (i < revealed || c === ' ') {
          return (
            <span
              key={i}
              className={letterClass}
              style={letterClass ? { '--i': i + offset } : undefined}
              aria-hidden="true"
            >
              {c === ' ' && letterClass ? '\u00A0' : c}
            </span>
          );
        }
        const aleatoire = CARACTERES[Math.floor(Math.random() * CARACTERES.length)];
        return <span key={i} className="scramble__char" aria-hidden="true">{aleatoire}</span>;
      })}
    </span>
  );
}

function CertCarousel() {
  const [index, setIndex] = useState(0);
  const [pause, setPause] = useState(false);
  const total = certifications.length;

  useEffect(() => {
    if (pause || total < 2) return undefined;
    const timer = setTimeout(() => setIndex((i) => (i + 1) % total), DUREE);
    return () => clearTimeout(timer);
  }, [index, pause, total]);

  const prev = (index - 1 + total) % total;

  return (
    <div
      className={`cert-carousel ${pause ? 'is-paused' : ''}`}
      onMouseEnter={() => setPause(true)}
      onMouseLeave={() => setPause(false)}
    >
      {/* Anneau extérieur qui tourne */}
      <span className="cert-carousel__orbit" aria-hidden="true"></span>

      {/* Anneau de progression (se remplit pendant l'affichage) */}
      <svg className="cert-carousel__progress" viewBox="0 0 100 100" aria-hidden="true">
        <circle
          key={index}
          cx="50"
          cy="50"
          r="48"
          style={{ animationDuration: `${DUREE}ms` }}
        />
      </svg>

      {/* Cercle central — miroir de la photo */}
      <div className="cert-carousel__circle">
        {certifications.map((cert, i) => (
          <a
            key={cert.alt}
            href={cert.href}
            target="_blank"
            rel="noreferrer"
            className={
              'cert-carousel__slide' +
              (i === index ? ' is-active' : '') +
              (i === prev && total > 1 ? ' is-prev' : '')
            }
            aria-hidden={i !== index}
            tabIndex={i === index ? 0 : -1}
            title={cert.alt}
          >
            <img src={cert.img} alt={cert.alt} />
          </a>
        ))}
      </div>

      {/* Nom + points de navigation */}
      <div className="cert-carousel__info">
        <span key={index} className="cert-carousel__label">
          {certifications[index].alt}
        </span>
        <div className="cert-carousel__dots">
          {certifications.map((cert, i) => (
            <button
              key={cert.alt}
              type="button"
              className={i === index ? 'is-active' : ''}
              onClick={() => setIndex(i)}
              aria-label={cert.alt}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Header({ onOpenCarte }) {
  const t = useAutoTranslate({ fr, en });

  return (
    <header className="header" id="page-top">
      <div className="header__bg"></div>
      <div className="header__content">
        {/* Photo + animation (miroir du carrousel) */}
        <div className="header__photo-wrap">
          <span className="photo-orbit" aria-hidden="true"></span>
          <svg className="photo-arc" viewBox="0 0 100 100" aria-hidden="true">
            <circle cx="50" cy="50" r="48" />
          </svg>
          <img
            className="header__photo"
            src="assets/img/photo.png"
            alt={`${t('prenom')} ${t('nom')}`}
          />
        </div>

        <div className="header__text">
          <p className="header__sub">
            <Typewriter text={`// ${t('bienvenue')}`} />
          </p>
          <AnimatedName prenom={t('prenom')} nom={t('nom')} />
          <div className="header__line"></div>
          <p className="header__role">
            <ScrambleText text={t('role')} />
          </p>
          <div className="header__ctas">
            <a href="#moi" className="btn-primary">{t('cta_decouvrir')}</a>
            <a href="#portfolio" className="btn-secondary">{t('cta_projets')}</a>
            <a href="#comp" className="btn-secondary btn-cv">{t('cta_cv')}</a>
            <a href="#contact" className="btn-secondary">{t('cta_contact')}</a>
          </div>
        </div>

        <CertCarousel />
      </div>

      {/* Bouton carte — visible uniquement sur mobile dans le header */}
      <button
        className="header__carte-btn"
        onClick={onOpenCarte}
        aria-label="Ouvrir la carte de visite"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
        </svg>
      </button>
    </header>
  );
}

export default Header;
